/**
 * Infinite scroll for the search results page and collection pages. 2026-09-11.
 *
 * Aaron: "when they press Enter and get taken to the search page, that also needs to
 * be infinite." Both pages already paginate the same way - a .product-grid of
 * .product-block cards and a .pagination__next link inside [data-ajax-container] -
 * so one script serves both.
 *
 * HOW: a sentinel sits after the grid. When it scrolls into view, the NEXT page's
 * section is fetched (section rendering API, same URL the pagination link points
 * at), its product cards are appended to the live grid, and the next link is updated
 * from the fetched page. Repeats until there is no next page.
 *
 * The page number is written into the address bar (replaceState) so a refresh or a
 * shared link lands where the shopper was.
 *
 * The numbered pagination stays in the markup for anyone without JavaScript; it is
 * hidden by class once this takes over.
 *
 * SEARCH RESULTS, 2026-09-15: sales order ACROSS pages. Shopify hands the theme
 * 48 text-matched candidates per page and has no sales sort for search, so the
 * server's order (search-rank.liquid) was only within a page - a never-sold
 * listing on page 1 sat above a seller on page 2. When the section carries a
 * .jhr-rank-keys list (one search-rank key per card, in order; never when the
 * shopper chose a sort) and the landing page is page 1, pages 2 and 3 are fetched straight after load
 * and every card NOT yet on screen is re-sorted with them by key. Cards the
 * shopper can already see never move. Load-more then carries on from page 4.
 * Same 144-candidate window as the dropdown. A page that fails to fetch is NOT
 * skipped: the merge stops before it and load-more retries it in order.
 *
 * The theme's ajax filtering swaps the grid, the pager and our sentinel out when
 * a filter or sort is tapped; a MutationObserver notices the grid is gone and
 * starts again on the new one (so a filtered list is not stuck at page 1).
 *
 * BACK RETURNS YOU TO THE PRODUCT YOU TAPPED (2026-09-15). Aaron: "I scrolled
 * down, went into another product, went back, and it took me to the first
 * product, not the second." When the browser cannot serve the page from its
 * back-forward cache (it often cannot, and our ?page=N rewrite then lands a
 * fresh load on page N alone), the list the shopper had built - pages 1-3
 * merged, 4, 5... appended - was gone. So a tap on any product saves the whole
 * grid as it stood (sessionStorage, this browser only, 30 minutes) plus the
 * tapped product and where the next page would come from; a Back onto the same
 * list puts it all straight back and scrolls the tapped product into view.
 */
(function () {
  'use strict';

  var MERGE_PAGES = 3;
  // Pages load on their own up to and including this one; from then on the
  // shopper taps "Show more". 2026-09-15, Aaron: with everything loading on
  // its own the text under the grid (and the footer) kept moving away as you
  // scrolled towards it. 2026-09-16, Aaron: "a maximum of 3 pages" - the
  // merge already holds pages 1-3, so the button appears straight after it.
  var AUTO_PAGES = 3;
  // A page can come back with NO cards while Shopify still has more: the theme
  // leaves out sold-out, no-photo and off-topic (head noun missing) products,
  // and a three-word search gets pages of "any two words" leftovers before the
  // rest of the real matches. 2026-09-17, Aaron: "horse riding boots" stopped at
  // 24 boots. So an empty page is skipped, up to this many in a row, and only a
  // missing "next" link ends the list.
  var EMPTY_PAGES_MAX = 8;
  function pageOf(url) {
    try { return parseInt(new URL(url, location.href).searchParams.get('page') || '1', 10) || 1; } catch (e) { return 1; }
  }

  // ---- Back-restore -------------------------------------------------------
  var RESTORE_KEY = 'jhr_grid_restore';
  // the same list = same page and query/filters, whatever ?page= says
  function listKey() {
    try {
      var u = new URL(location.href);
      u.searchParams.delete('page');
      u.hash = '';
      return u.pathname + '?' + u.searchParams.toString();
    } catch (e) { return location.pathname; }
  }
  function readSnapshot() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (!nav || nav.type !== 'back_forward') return null;    // only ever on Back
      var s = JSON.parse(sessionStorage.getItem(RESTORE_KEY) || 'null');
      if (!s || s.key !== listKey() || Date.now() - s.t > 30 * 60 * 1000) return null;
      sessionStorage.removeItem(RESTORE_KEY);
      return s;
    } catch (e) { return null; }
  }
  function saveSnapshot(grid, handle, nextUrl, done) {
    try {
      sessionStorage.setItem(RESTORE_KEY, JSON.stringify({
        key: listKey(), html: grid.innerHTML, handle: handle, nextUrl: nextUrl, done: done, t: Date.now()
      }));
    } catch (e) { /* quota or private mode - Back works the ordinary way */ }
  }
  // cards put back from a snapshot must not wait for the theme's scroll-in
  // animation again (they carry cc-animate-init from their first life, which the
  // animation script treats as "already handled" - they would stay invisible)
  function unanimate(root) {
    Array.prototype.slice.call(root.querySelectorAll('[data-cc-animate]')).forEach(function (el) {
      el.removeAttribute('data-cc-animate');
      el.classList.remove('cc-animate-init', 'cc-animate-in');
    });
  }

  // The head-noun part of a search-rank key: "[missing]|..." where missing is
  // 6 x head-noun-absent + the other missing words (search-rank.liquid).
  // Pins and boosts ("!|0", "!|1") always count as carrying the head noun.
  function headMissing(key) {
    var k = String(key);
    if (k.charAt(0) === '!') return false;
    return (parseInt(k, 10) || 0) >= 6;
  }
  function keysOf(root) {
    var s = root.querySelector('.jhr-rank-keys');
    if (!s) return null;
    try { var k = JSON.parse(s.textContent); return Array.isArray(k) ? k : null; } catch (e) { return null; }
  }
  function handleOf(card) {
    var a = card.querySelector('a[href*="/products/"]');
    return a ? (a.getAttribute('href').split('/products/')[1] || '').split('?')[0].split('#')[0] : '';
  }
  // the key without its last segment (the row's index WITHIN its page), so rows
  // that tie on every signal fall back to page order, then position - Shopify's
  // relevance across the pages, not p1#0, p2#0, p3#0, p1#1...
  function keyBase(k) {
    var p = String(k).lastIndexOf('|');
    return p > 0 ? String(k).slice(0, p) : String(k);
  }
  function byKey(a, b) {
    var ka = keyBase(a.key);
    var kb = keyBase(b.key);
    if (ka < kb) return -1;
    if (ka > kb) return 1;
    return (a.page - b.page) || (a.i - b.i);
  }

  function init(section) {
    var grid = section.querySelector('.product-grid');
    var pager = section.querySelector('.pagination');
    if (!grid || !pager || grid.dataset.infiniteScroll) return;
    grid.dataset.infiniteScroll = '1';
    var next = pager.querySelector('.pagination__next');
    if (!next) return;                                        // single page - nothing to do

    var sectionId = (section.id || '').replace(/^shopify-section-/, '');
    if (!sectionId) return;

    var nextUrl = next.getAttribute('href');
    var busy = false;
    var done = false;
    // 2026-09-16: search-rank.liquid drops products without the head noun only
    // when a PAGE holds 3+ that have it, so a later page with two hats and 32
    // boots let the boots through ("childs hats" page 2). The first page
    // decides for the whole list: any head-noun product there = every later
    // page is held to the same rule here, before its cards are added.
    var strict = false;
    // highest page on screen; the button takes over once it reaches AUTO_PAGES
    var loadedThrough = pageOf(location.href);

    section.classList.add('infinite-scroll--active');

    // Back onto a list the shopper was scrolling: put it back before anything
    // else runs, and skip the page 1-3 merge (the snapshot already holds it).
    var snap = readSnapshot();
    var restoredCard = null;
    if (snap && snap.html) {
      grid.innerHTML = snap.html;
      unanimate(grid);
      nextUrl = snap.nextUrl || null;
      done = !!snap.done || !nextUrl;
      if (nextUrl) loadedThrough = Math.max(loadedThrough, pageOf(nextUrl) - 1);
      if (snap.handle) {
        var link = grid.querySelector('a[href*="/products/' + snap.handle + '"]');
        restoredCard = link ? link.closest('.product-block') : null;
      }
      try { history.scrollRestoration = 'manual'; } catch (e) { /* fine */ }
    }
    // Results page: the honest count (2026-09-16). Shopify's number is any-word
    // matches capped at 1,000; the grid holds the real ones. "Showing 62
    // results", with a + while more pages remain.
    var countEl = document.querySelector('[data-search-count]');
    function sayCount() {
      if (!countEl || !grid.isConnected) return;
      var n = grid.querySelectorAll(':scope > .product-block').length;
      var more = !done && !!nextUrl;
      countEl.textContent = 'Showing ' + n + (more ? '+' : '') + (n === 1 && !more ? ' result' : ' results');
    }
    // A tap on a product notes WHICH product (cheap); the grid itself is saved
    // as the page leaves. 2026-09-16: serialising ~150 cards inside the click
    // was a few hundred ms of nothing on a phone before the page changed -
    // Aaron: "I'm having to double-click".
    var tappedHandle = null;
    grid.addEventListener('click', function (evt) {
      var a = evt.target && evt.target.closest ? evt.target.closest('a[href*="/products/"]') : null;
      if (!a || !grid.contains(a)) return;
      tappedHandle = handleOf(a.closest('.product-block') || a.parentElement) || '';
    }, true);
    window.addEventListener('pagehide', function () {
      if (tappedHandle === null || !grid.isConnected) return;
      saveSnapshot(grid, tappedHandle, nextUrl, done);
      tappedHandle = null;
    });

    // Fetch pages 2..MERGE_PAGES and sort them in with page 1 (see header note).
    // Only from a page-1 landing: on ?page=4 (refresh, shared link) we simply
    // continue from page 5 as before, or page 4 would be loaded twice. Resolves
    // whichever way it goes, leaving nextUrl / done pointing at the right page.
    function mergeAhead() {
      var keys1 = keysOf(section);
      var cards1 = Array.prototype.slice.call(grid.querySelectorAll(':scope > .product-block'));
      var landing = 1;
      try { landing = parseInt(new URL(location.href).searchParams.get('page') || '1', 10) || 1; } catch (e) { landing = 1; }
      if (landing !== 1 || !keys1 || keys1.length !== cards1.length || !nextUrl) return Promise.resolve();
      var base = new URL(nextUrl, location.href);
      var pageUrl = function (p) {
        var u = new URL(base.toString());
        u.searchParams.set('page', String(p));
        return u;
      };
      var fetches = [];
      for (var p = 2; p <= MERGE_PAGES; p++) {
        var u = pageUrl(p);
        u.searchParams.set('section_id', sectionId);
        fetches.push(fetch(u.toString()).then(function (r) { return r.ok ? r.text() : null; }).catch(function () { return null; }));
      }
      return Promise.all(fetches).then(function (htmls) {
        if (!grid.isConnected) return;                        // a filter/sort swap beat us to it
        var pool = cards1.map(function (c, i) { return { el: c, key: keys1[i], page: 1, i: i }; });
        strict = keys1.filter(function (k) { return !headMissing(k); }).length >= 1;
        var seen = {};
        cards1.forEach(function (c) { var h = handleOf(c); if (h) seen[h] = true; });
        var lastGood = 1;       // last page merged in
        var lastDoc = null;
        var stoppedAt = 0;      // first page that FAILED (null html) - load-more retries from here
        var ranOut = false;     // a page came back with no cards: nothing beyond it
        for (var j = 0; j < htmls.length; j++) {
          var pageNo = j + 2;
          if (htmls[j] === null) { stoppedAt = pageNo; break; }
          var doc = new DOMParser().parseFromString(htmls[j], 'text/html');
          var cards = Array.prototype.slice.call(doc.querySelectorAll('.product-grid > .product-block'));
          if (!cards.length) {
            if (!doc.querySelector('.pagination__next')) { ranOut = true; break; }
            lastGood = pageNo; lastDoc = doc;              // nothing to show here; carry on from its "next"
            continue;
          }
          var keys = keysOf(doc);
          if (!keys || keys.length !== cards.length) { stoppedAt = pageNo; break; }   // treat like a failure: load it plainly later
          cards.forEach(function (c, i) {
            if (strict && headMissing(keys[i])) return;     // not what was asked for
            var h = handleOf(c);
            if (h) { if (seen[h]) return; seen[h] = true; }
            pool.push({ el: c, key: keys[i], page: pageNo, i: i });
          });
          lastGood = pageNo;
          lastDoc = doc;
        }
        // what the shopper can already see stays put; everything else re-sorts
        var frozen = [];
        cards1.forEach(function (c) { if (c.getBoundingClientRect().top < window.innerHeight) frozen.push(c); });
        var rest = pool.filter(function (x) { return frozen.indexOf(x.el) === -1; });
        rest.sort(byKey);
        // The promo strips (snippets/jhr-slide-effects.liquid) sit between rows
        // as grid children; moving the cards would leave them stacked together.
        // Take them out and let that script's own observer put them back on
        // the merged grid (it re-runs when the product count changes).
        Array.prototype.slice.call(grid.querySelectorAll(':scope > .jhr-usp')).forEach(function (b) { b.remove(); });
        grid.__jhrCount = 0;
        rest.forEach(function (x) { grid.appendChild(x.el); });
        if (stoppedAt) { nextUrl = pageUrl(stoppedAt).toString(); }            // retry that page in order
        else if (ranOut) { nextUrl = null; done = true; }
        else if (lastDoc) {
          var n = lastDoc.querySelector('.pagination__next');
          nextUrl = n ? n.getAttribute('href') : null;
          if (!nextUrl) done = true;
        }
        // pages 1..MERGE_PAGES are on screen now (or up to the one that failed)
        loadedThrough = Math.max(loadedThrough, (stoppedAt || MERGE_PAGES + 1) - 1);
      });
    }

    var sentinel = document.createElement('div');
    sentinel.className = 'infinite-scroll__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    var status = document.createElement('div');
    status.className = 'infinite-scroll__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    grid.insertAdjacentElement('afterend', sentinel);
    sentinel.insertAdjacentElement('afterend', status);
    var moreWrap = document.createElement('div');
    moreWrap.className = 'infinite-scroll__more-wrap';
    moreWrap.hidden = true;
    var moreBtn = document.createElement('button');
    moreBtn.type = 'button';
    moreBtn.className = 'btn infinite-scroll__more';
    moreBtn.textContent = 'Show more products';
    moreWrap.appendChild(moreBtn);
    status.insertAdjacentElement('afterend', moreWrap);
    moreBtn.addEventListener('click', function () { loadNext(true); });
    function autoAllowed() { return loadedThrough < AUTO_PAGES; }
    // the button shows whenever there is more and the automatic run is over
    function syncMore() { moreWrap.hidden = done || !nextUrl || autoAllowed() || busy; }

    // 2 Oct 2026 (Aaron, The Secret Tack Room: "it'll load maybe 3 sometimes, 2 sometimes, 5 sometimes"): every page
    // of 20 loses its sold-out products before it reaches the screen (main-collection hide_sold_out), so pages showed
    // 9, 9, 9, 5, 4, 3, 7, 10, 3, 4, 2, 14, 5 (MEASURED). Each load now keeps fetching the next page until it holds at
    // least BATCH cards (or the list ends, or MAX_PAGES_PER_LOAD pages), then adds them all in one go. Empty pages are
    // skipped the same way (still at most EMPTY_PAGES_MAX in a row).
    var BATCH = 20;
    var MAX_PAGES_PER_LOAD = 10;
    var emptyRun = 0;           // empty pages in a row (see EMPTY_PAGES_MAX)
    function loadNext(byTap) {
      if (busy || done || !nextUrl) { syncMore(); return; }
      if (!byTap && !autoAllowed()) { syncMore(); return; }
      syncMore();
      if (!grid.isConnected || !sentinel.isConnected) { done = true; return; }   // swapped away by a filter
      busy = true;
      status.textContent = 'Loading more…';
      var collected = [];
      var pages = 0;
      var lastUrl = null;
      function step() {
        var url = new URL(nextUrl, location.href);
        url.searchParams.set('section_id', sectionId);
        pages += 1;
        return fetch(url.toString())
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            if (!grid.isConnected) { done = true; return; }
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var cards = Array.prototype.slice.call(doc.querySelectorAll('.product-grid > .product-block'));
            if (strict) {
              var keys = keysOf(doc);
              if (keys && keys.length === cards.length) cards = cards.filter(function (c, i) { return !headMissing(keys[i]); });
            }
            var newNext = doc.querySelector('.pagination__next');
            loadedThrough = Math.max(loadedThrough, pageOf(url.toString()));
            lastUrl = url;
            emptyRun = cards.length ? 0 : emptyRun + 1;
            cards.forEach(function (c) { collected.push(c); });
            nextUrl = newNext && emptyRun < EMPTY_PAGES_MAX ? newNext.getAttribute('href') : null;
            if (!nextUrl) { done = true; return; }
            // keep going for a full batch (up to MAX_PAGES_PER_LOAD pages) - and never stop on nothing: a tap that adds
            // no products is worse than a short wait (the end of the list stops it anyway)
            if (collected.length < BATCH && (pages < MAX_PAGES_PER_LOAD || collected.length === 0)) return step();
          });
      }
      step()
        .catch(function () { /* a failed page: show what we have, the next tap tries again */ })
        .then(function () {
          if (grid.isConnected && collected.length) {
            collected.forEach(function (c) { grid.appendChild(c); });
            // remember where we are for refresh / share
            try {
              var page = lastUrl && lastUrl.searchParams.get('page');
              if (page) { var here = new URL(location.href); here.searchParams.set('page', page); history.replaceState(history.state, '', here.toString()); }
            } catch (e) { /* fine */ }
          }
          status.textContent = '';
          busy = false;
          syncMore(); sayCount(); if (!done) maybeLoadMore();
        });
    }

    var io = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) loadNext(false);
    }, { rootMargin: '600px 0px' });                         // start well before the bottom
    // if the first page is shorter than the screen, keep going until it is not
    function maybeLoadMore() {
      if (!sentinel.isConnected) return;
      var r = sentinel.getBoundingClientRect();
      if (r.top < window.innerHeight + 600) loadNext(false); else syncMore();
    }
    // search results: sort pages 1-3 together first, THEN start watching the bottom
    // (a restored list skips the merge - it is already merged)
    (snap ? Promise.resolve() : mergeAhead()).then(function () {
      if (!sentinel.isConnected) return;                      // grid was swapped meanwhile
      sayCount();
      if (restoredCard) {
        // scroll the tapped product to the middle, and again as images settle
        var tries = 0;
        var settle = function () {
          if (restoredCard.isConnected) restoredCard.scrollIntoView({ block: 'center', behavior: 'instant' });
          if (++tries < 8) setTimeout(settle, 120);
          else { try { history.scrollRestoration = 'auto'; } catch (e) { /* fine */ } }
        };
        settle();
      }
      io.observe(sentinel);
      maybeLoadMore();
    });

    // The theme's ajax filtering replaces the grid (and our sentinel) wholesale.
    // Watch for that and start again on whatever replaced it.
    var mo = new MutationObserver(function () {
      if (grid.isConnected) return;
      mo.disconnect();
      io.disconnect();
      done = true;
      requestAnimationFrame(function () { init(section); });
    });
    mo.observe(section, { childList: true, subtree: true });

    // manual trigger - support/testing only; the observer is what runs in normal use
    window.jhrInfiniteScroll = { loadNext: loadNext, isDone: function () { return done; }, state: function () { return { loadedThrough: loadedThrough, done: done, busy: busy, nextUrl: nextUrl, autoPages: AUTO_PAGES }; } };
  }

  function boot() {
    document.querySelectorAll('[id^="shopify-section-"]').forEach(function (s) {
      if (s.querySelector('.product-grid') && s.querySelector('.pagination')) init(s);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
